import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  CanonicalSkillAutoCompleteResultItem,
  JobseekerSkillViewModel,
  SkillAutoCompleteResult,
  UserTypeEnum,
} from '@hrra/common-models';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  SkillsFilterDialogComponent,
  SkillsFilterDialogData,
} from './skills-filter-dialog.component';

const SKILL_BOX_ROW_HEIGHT_REM = 1.875;
const SKILL_BOX_ROW_GAP_REM = 0.5;

//presentational skills filter: autocomplete input plus the chosen-skill boxes underneath.
//the box list is clamped to maxVisibleRows and an "all" link opens a popup with the full list.
//every mutation is emitted as the complete new list so hosts need a single handler
@Component({
    selector: 'hrra-skills-filter',
    templateUrl: './skills-filter.component.html',
    styleUrls: ['./skills-filter.component.scss'],
    standalone: false
})
export class SkillsFilterComponent implements OnInit, OnDestroy, AfterViewChecked {
  private destroy$ = new Subject<void>();

  @Input() placeholder: string;
  @Input() skills: JobseekerSkillViewModel[] | null;
  @Input() suggestions: SkillAutoCompleteResult | null;
  //title of the popup opened from the "all" link
  @Input() allSkillsDialogTitle: string;
  @Input() maxVisibleRows = 3;

  @Output() onQueryChange = new EventEmitter<string>();
  @Output() onSkillsChange = new EventEmitter<JobseekerSkillViewModel[]>();

  @ViewChild('chosenSkills') chosenSkills: ElementRef<HTMLElement>;

  public skillInput = new UntypedFormControl('');
  public userType: any = UserTypeEnum;
  //true once the clamped box list no longer fits, which is what reveals the "all" link
  public readonly isClamped = signal(false);
  //the query alone must not keep the panel open: it also has to survive focus leaving the input
  public readonly isSuggestionsOpen = signal(false);

  constructor(
    private dialog: MatDialog,
    private elementRef: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  //stays a getter rather than a computed: the query lives in the form control and the
  //suggestions arrive through a plain input, so neither would invalidate a cached computed
  public get isSuggestionsVisible(): boolean {
    return this.isSuggestionsOpen() && !!this.skillInput.value && !!this.suggestions;
  }

  //height of maxVisibleRows box rows, gaps included; keeps the clamp in step with the chip metrics
  public get clampMaxHeight(): string {
    let rows = Math.max(1, this.maxVisibleRows);
    let height = rows * SKILL_BOX_ROW_HEIGHT_REM + (rows - 1) * SKILL_BOX_ROW_GAP_REM;

    return `${height}rem`;
  }

  ngOnInit(): void {
    this.skillInput.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((query) => this.onQueryChange.emit(query ?? ''));
  }
  
  ngAfterViewChecked(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    let element = this.chosenSkills?.nativeElement;
    let isClamped = element !== undefined && element.scrollHeight > element.clientHeight;

    if (isClamped !== this.isClamped()) {
      this.isClamped.set(isClamped);
    }
  }

  openSuggestions() {
    this.isSuggestionsOpen.set(true);
  }

  closeSuggestions() {
    this.isSuggestionsOpen.set(false);
  }

  //focus moving to another filter must drop the panel; mousedown runs before the suggestion
  //click, so a pick inside the component still lands before the panel is taken away
  @HostListener('document:mousedown', ['$event.target'])
  @HostListener('document:focusin', ['$event.target'])
  onDocumentInteraction(target: EventTarget | null) {
    if (!this.isSuggestionsOpen()) {
      return;
    }

    if (target instanceof Node && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.closeSuggestions();
  }

  selectSkill(skill: CanonicalSkillAutoCompleteResultItem) {
    let chosen = this.skills ?? [];

    this.skillInput.setValue('', { emitEvent: false });

    if (chosen.some((s) => s.code === skill.code)) {
      return;
    }

    let selected: JobseekerSkillViewModel = {
      canonicalSkillId: skill.skillId,
      code: skill.code,
      name: skill.name,
      isCustom: false,
    };

    this.onSkillsChange.emit([...chosen, selected]);
  }

  removeSkill(index: number) {
    this.onSkillsChange.emit((this.skills ?? []).filter((_, i) => i !== index));
    this.onQueryChange.emit(this.skillInput.value ?? '');
  }

  showAllSkills() {
    let dialogRef = this.dialog.open<
      SkillsFilterDialogComponent,
      SkillsFilterDialogData,
      JobseekerSkillViewModel[]
    >(SkillsFilterDialogComponent, {
      data: { title: this.allSkillsDialogTitle, skills: this.skills ?? [] },
      panelClass: 'skills-filter-dialog-panel',
      width: '39rem',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((skills) => {
        if (skills !== undefined) {
          this.onSkillsChange.emit(skills);
        }
      });
  }

  //splits a suggestion name into the part matching the query (shown in bold) and the rest
  getMatchSplit(name: string): { pre: string; match: string; post: string } {
    let query = (this.skillInput.value ?? '').trim();
    let index = query ? name.toLowerCase().indexOf(query.toLowerCase()) : -1;
    if (index < 0) {
      return { pre: name, match: '', post: '' };
    }
    return {
      pre: name.slice(0, index),
      match: name.slice(index, index + query.length),
      post: name.slice(index + query.length),
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
